import {
  Button,
  Label,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Progress,
  Switch,
} from "@repo/ui";
import { useFormStore } from "../../stores/formStore";
import { useFormPageNavigation } from "../../hooks/useFormPageNavigation";
import { FormTransitionOverlay } from "../../components/FormTransitionOverlay";
import { cn } from "@repo/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "convex/react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api } from "@convex/_generated/api";
import { TemplateFormEnum, TypeEvalEnum } from "@convex/schema";
import {
  type EgogrammeReponses,
  type FormTemplateProps,
  isBackendError,
} from "../../lib/types";
import {
  EGOGRAMME_QUESTIONS_PER_PAGE,
  egogrammeQuestions,
} from "./egogramme-data";
// Construit le schéma Zod à partir des questions
const schema = z.object(
  Object.fromEntries(
    egogrammeQuestions.map((q) => [q.name, z.boolean().default(false)]),
  ),
);

type FormData = z.infer<typeof schema>;

export function CandidateEgogrammeTemplate({
  token,
  parcoursId,
  editable,
  evaluation,
  onComplete,
}: FormTemplateProps) {
  const { isSubmitting, setIsSubmitting } = useFormStore();

  const submitEvaluation = useMutation(api.evaluations.soumettreEvaluation);
  const updateResponses = useMutation(api.evaluations.mettreAJourReponses);

  const totalPages = Math.ceil(
    egogrammeQuestions.length / EGOGRAMME_QUESTIONS_PER_PAGE,
  );

  // Convertit les réponses en booléens pour le formulaire
  const reponses = evaluation.donnees.reponses as EgogrammeReponses;
  const convertedInitialResponses = Object.fromEntries(
    Object.entries(reponses).map(([key, value]) => [key, value.response]),
  );

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: convertedInitialResponses as FormData,
  });

  // Construit l'objet donnees destiné au backend
  const buildDonnees = (data: FormData, pageActuelle?: number) => ({
    type: TypeEvalEnum.FORM as const,
    template: TemplateFormEnum.EGOGRAMME,
    ...(pageActuelle !== undefined && { pageActuelle }),
    reponses: egogrammeQuestions.reduce<EgogrammeReponses>(
      (acc, q) => ({
        ...acc,
        [q.name]: {
          name: q.name,
          label: q.label,
          profil: q.profil,
          response: data[q.name as keyof FormData],
        },
      }),
      {},
    ),
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      const result = await submitEvaluation({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data),
      });

      if (isBackendError(result)) {
        console.error("Erreur:", result.erreur);
      } else {
        onComplete?.();
      }
    } catch (error) {
      console.error("Erreur lors de la soumission:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveResponses = async (data: FormData, pageActuelle: number) => {
    try {
      await updateResponses({
        evaluationId: evaluation._id,
        parcoursId,
        token,
        donnees: buildDonnees(data, pageActuelle),
      });
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
    }
  };

  const { currentPage, isNavigating, showTransitionOverlay, goToPage } =
    useFormPageNavigation<FormData>({
      pageCount: totalPages,
      pageActuelle: evaluation.donnees.pageActuelle,
      save: saveResponses,
    });

  const startIndex = currentPage * EGOGRAMME_QUESTIONS_PER_PAGE;
  const endIndex = startIndex + EGOGRAMME_QUESTIONS_PER_PAGE;
  const currentQuestions = egogrammeQuestions.slice(startIndex, endIndex);

  const progressPercentage = ((currentPage + 1) / totalPages) * 100;

  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === totalPages - 1;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6 mx-auto container py-8"
      >
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">Egogramme</h1>
          <p className="text-muted-foreground">
            Test de personnalite - 140 questions
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>
              Page {currentPage + 1} sur {totalPages}
            </span>
            <span>
              Questions {startIndex + 1}-
              {Math.min(endIndex, egogrammeQuestions.length)} sur{" "}
              {egogrammeQuestions.length}
            </span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        <div
          className={cn(
            "space-y-4 transition-opacity",
            isNavigating && "pointer-events-none opacity-50",
          )}
          aria-busy={isNavigating}
        >
          {currentQuestions.map((q, index) => (
            <FormField
              key={q.name}
              control={form.control}
              name={q.name}
              render={({ field }) => (
                <FormItem className="flex items-center justify-between p-4 border rounded-surface">
                  <Label
                    htmlFor={q.name}
                    className="flex-1 cursor-pointer pr-4"
                  >
                    <div className="flex flex-col">
                      <div>
                        <span className="text-muted-foreground mr-2">
                          {startIndex + index + 1}.
                        </span>
                        {q.label}
                      </div>
                      <FormMessage />
                    </div>
                  </Label>
                  <FormControl>
                    <Switch
                      id={q.name}
                      checked={field.value}
                      disabled={!editable}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          ))}
        </div>

        <div className="flex justify-between gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => goToPage(currentPage - 1, form.getValues())}
            disabled={isFirstPage || isNavigating}
            className="flex-1"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Precedent
          </Button>

          {isLastPage ? (
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting || isNavigating || !editable}
            >
              {isSubmitting ? "Envoi en cours..." : "Soumettre"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => goToPage(currentPage + 1, form.getValues())}
              disabled={isNavigating}
              className="flex-1"
            >
              {isNavigating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  Suivant
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>

        <FormTransitionOverlay open={showTransitionOverlay} />
      </form>
    </Form>
  );
}
